import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Safety net: if a stale lazy-chunk slips through (e.g. after a new
    // deployment), auto-reload once to fetch the fresh manifest instead of
    // leaving the user on the error screen.
    const msg = (error && (error.message || error.toString())) || '';
    const isChunkError =
      (error && error.name === 'ChunkLoadError') ||
      /Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(msg);

    if (isChunkError) {
      const KEY = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(KEY)) || 0;
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }

    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    // Use window.location instead of Link to avoid Router context issues
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="text-red-600" size={32} />
            </div>
            
            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Oups ! Une erreur est survenue
            </h1>
            <p className="text-gray-600 mb-6">
              La page n&apos;a pas pu se charger correctement. 
              Cela peut arriver si la connexion est instable ou si le site est en cours de mise à jour.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                onClick={this.handleReload}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <RefreshCw className="mr-2" size={18} />
                Actualiser la page
              </Button>
              <Button
                variant="outline"
                className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                onClick={this.handleGoHome}
              >
                <Home className="mr-2" size={18} />
                Retour à l&apos;accueil
              </Button>
            </div>

            {/* Contact Option */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3">
                Le problème persiste ?
              </p>
              <a
                href="https://wa.me/33782586685"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium text-sm"
              >
                <MessageCircle size={16} />
                Contactez-nous sur WhatsApp
              </a>
            </div>

            {/* Error Details (dev only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Détails techniques
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-40 text-red-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
